import {
    parsePhoneNumberFromString,
    isSupportedCountry,
    type CountryCode,
} from 'libphonenumber-js';

interface PhoneParseResult {
    /** E.164 format suitable for tel: URIs, e.g. "+6492730040" */
    e164: string;
    /** Ready-to-use tel: URI, e.g. "tel:+6492730040" */
    telUri: string;
}

/** ITU E.161 telephone keypad: converts an ASCII letter to its digit (2–9). */
const alphaToDigit = (ch: string): string => {
    const c = ch.toUpperCase();
    if (c >= 'A' && c <= 'C') return '2';
    if (c >= 'D' && c <= 'F') return '3';
    if (c >= 'G' && c <= 'I') return '4';
    if (c >= 'J' && c <= 'L') return '5';
    if (c >= 'M' && c <= 'O') return '6';
    if (c >= 'P' && c <= 'S') return '7';
    if (c >= 'T' && c <= 'V') return '8';
    if (c >= 'W' && c <= 'Z') return '9';
    return ch;
};

/**
 * Known freephone / shared-cost prefixes mapped to their valid total digit
 * counts (including the prefix).  Lengths are listed shortest-first; the
 * algorithm prefers the shortest valid truncation for overflow because
 * production hint data confirms the shorter format is almost always correct.
 *
 * IMPORTANT — ordering: longer prefixes must appear before shorter ones that
 * share the same leading digits (e.g. `0800` before `800`, `1888` before
 * `888`) so the first `startsWith` match is the most specific.
 */
const VANITY_PREFIX_LENGTHS: [prefix: string, validTotalDigits: number[]][] = [
    // NZ freephone / shared-cost
    ['0800', [10, 11]],
    ['0508', [10]],
    // AU shared-cost
    ['1300', [10]],
    // AU freephone (10 digits) / US 1-800 domestic format (11 digits)
    ['1800', [10, 11]],
    // US toll-free with leading 1 (domestic format: 1 + 3-digit prefix + 7 digits = 11)
    ['1888', [11]],
    ['1877', [11]],
    ['1866', [11]],
    ['1855', [11]],
    ['1844', [11]],
    ['1833', [11]],
    // US toll-free without leading 1 (3-digit prefix + 7 digits = 10)
    ['800', [10]],
    ['888', [10]],
    ['877', [10]],
    ['866', [10]],
    ['855', [10]],
    ['844', [10]],
    ['833', [10]],
];

/**
 * Converts alpha characters to keypad digits and matches the resulting
 * digit string against known freephone prefix lengths.  Returns the
 * matched/truncated digit string, or `null` when no prefix matches.
 */
const attemptVanityConversion = (text: string): string | null => {
    const converted = text.replace(/[a-zA-Z]/g, alphaToDigit);
    const digits = converted.replace(/\D/g, '');

    for (const [prefix, validLengths] of VANITY_PREFIX_LENGTHS) {
        if (!digits.startsWith(prefix)) continue;

        if (validLengths.includes(digits.length)) {
            return digits;
        }

        for (const totalLen of validLengths) {
            if (digits.length > totalLen) {
                return digits.slice(0, totalLen);
            }
        }
    }

    return null;
};

/**
 * Converts a vanity/alpha freephone number (e.g. "0800 BELCANTO") into a
 * purely numeric string that libphonenumber-js can parse.
 *
 * Strategy:
 * 1. Strip parenthetical content (digit hints / alternate forms) and
 *    attempt keypad conversion on what remains.
 * 2. If stripping parentheses removed all the alpha content (e.g.
 *    "0800  (TILE DEPOT)"), fall back to removing only the parenthesis
 *    delimiters while keeping their content.
 * 3. Match against known freephone prefixes — exact-length matches are
 *    used as-is; overflow is truncated to the shortest valid length.
 *
 * Returns `null` when conversion is not possible.
 */
const normaliseVanityNumber = (raw: string): string | null => {
    if (!/[a-zA-Z]/.test(raw)) return null;

    const stripped = raw.replace(/\(.*?\)/g, '').trim();
    if (stripped) {
        const result = attemptVanityConversion(stripped);
        if (result) return result;
    }

    // Parenthetical content held all the alpha chars — keep the content,
    // just remove the delimiter characters themselves.
    const withoutDelimiters = raw.replace(/[()]/g, '').trim();
    if (withoutDelimiters !== stripped) {
        return attemptVanityConversion(withoutDelimiters);
    }

    return null;
};

const toResult = (
    parsed: ReturnType<typeof parsePhoneNumberFromString>,
): PhoneParseResult | null => {
    if (!parsed?.isValid()) return null;
    return { e164: parsed.number, telUri: `tel:${parsed.number}` };
};

/**
 * Attempts to parse a raw phone string into a dialable phone number.
 *
 * Returns a {@link PhoneParseResult} with the E.164 number and a `tel:` URI
 * when the input can be reliably parsed, or `null` when it cannot (e.g.
 * multi-number strings, URLs, plain text, etc.).
 *
 * Vanity freephone numbers (e.g. "0800 BELCANTO", "1300 EUROPE") are
 * converted to their numeric equivalents via ITU E.161 keypad mapping
 * before parsing.
 *
 * A `defaultCountry` hint is needed for domestic numbers that lack a `+`
 * prefix (e.g. "09 273 0040" requires "NZ" to resolve correctly).
 * The country string is validated at runtime — unsupported values are
 * silently ignored rather than causing an error.
 */
export const parsePhone = (
    raw: string,
    defaultCountry?: string,
): PhoneParseResult | null => {
    if (!raw.trim()) return null;

    const country =
        defaultCountry && isSupportedCountry(defaultCountry)
            ? (defaultCountry as CountryCode)
            : undefined;

    const direct = toResult(parsePhoneNumberFromString(raw, country));
    if (direct) return direct;

    const vanity = normaliseVanityNumber(raw);
    if (vanity) {
        const result = toResult(parsePhoneNumberFromString(vanity, country));
        if (result) return result;
    }

    return null;
};
